// FILE: src/components/ReleaseNotesView.js
//
// Admin → Release Notes view.  Renders the parsed releases.json (built
// from CHANGELOG.md by scripts/build-releases.js) as a date-descending
// timeline.  Each release shows version + date + title + summary +
// highlights inline; full Added / Changed / Fixed buckets are collapsed
// behind a single "Details" toggle to keep the page scannable.
//
// ── Props ─────────────────────────────────────────────────────────────────
//
//   onBack    function — return to AdminScreen.
//   releases  object[] (optional) — defaults to the bundled releases.json
//                       so the view works without any wiring.  Tests pass
//                       a fixture array for deterministic assertions.
//
// ── Why no markdown renderer ──────────────────────────────────────────────
//
// The build-releases parser strips markdown structure into plain text
// strings before this component sees them.  Inline emphasis like `code`
// or **bold** is rendered as literal characters here on purpose — adding
// a markdown library would be a new dep for very little gain on a feed
// the audience reads as a quick changelog rather than as documentation.

import React, { useState } from 'react';
import bundledReleases from '../../data/releases.json';

// ── Bucket display order (Keep-a-Changelog spec ordering) ────────────────

const BUCKET_ORDER = [
  'Added',
  'Changed',
  'Deprecated',
  'Removed',
  'Fixed',
  'Security',
];

function orderedBucketKeys(buckets) {
  const present = Object.keys(buckets || {});
  // Known buckets first, in spec order; then anything custom alphabetically.
  const known   = BUCKET_ORDER.filter((k) => present.includes(k));
  const unknown = present.filter((k) => !BUCKET_ORDER.includes(k)).sort();
  return [...known, ...unknown];
}

// ── Styles (inline, consistent with sibling admin views) ─────────────────

const styles = {
  wrapper: {
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    minHeight: '100vh',
    background: '#f5f5f5',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    background: '#1a1a2e',
    color: '#fff',
    padding: '14px 28px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flexShrink: 0,
  },
  backButton: {
    padding: '6px 14px',
    background: 'transparent',
    border: '1px solid #555',
    borderRadius: '4px',
    color: '#ccc',
    cursor: 'pointer',
    fontSize: '0.85rem',
    flexShrink: 0,
  },
  headerTitle: {
    fontSize: '1.1rem',
    fontWeight: 'bold',
    flex: 1,
    letterSpacing: '0.5px',
  },
  body: {
    flex: 1,
    padding: '32px 24px',
    maxWidth: '880px',
    margin: '0 auto',
    width: '100%',
    boxSizing: 'border-box',
  },
  emptyState: {
    background: '#fff',
    border: '1px dashed #d0d0d0',
    borderRadius: '8px',
    padding: '40px 24px',
    textAlign: 'center',
    color: '#777',
    fontSize: '0.95rem',
  },
  releaseCard: {
    background: '#fff',
    border: '1px solid #e2e2e2',
    borderRadius: '10px',
    padding: '20px 24px',
    marginBottom: '18px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
  },
  releaseHead: {
    display: 'flex',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    gap: '12px',
    marginBottom: '6px',
  },
  versionBadge: {
    display: 'inline-block',
    background: '#1a1a2e',
    color: '#fff',
    padding: '2px 10px',
    borderRadius: '14px',
    fontFamily: "'Consolas', 'Monaco', monospace",
    fontSize: '0.82rem',
    fontWeight: 700,
    letterSpacing: '0.4px',
    flexShrink: 0,
  },
  date: {
    fontSize: '0.82rem',
    color: '#888',
    fontFamily: "'Consolas', 'Monaco', monospace",
  },
  title: {
    fontSize: '1.05rem',
    fontWeight: 700,
    color: '#222',
    margin: 0,
    lineHeight: 1.35,
  },
  summary: {
    fontSize: '0.9rem',
    color: '#555',
    lineHeight: 1.55,
    margin: '8px 0 0',
  },
  highlightsBlock: {
    marginTop: '14px',
    padding: '12px 16px',
    background: '#f7f7fb',
    borderLeft: '3px solid #1a1a2e',
    borderRadius: '4px',
  },
  highlightsLabel: {
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.6px',
    color: '#1a1a2e',
    textTransform: 'uppercase',
    marginBottom: '6px',
  },
  bullets: {
    margin: '0',
    paddingLeft: '20px',
    fontSize: '0.88rem',
    color: '#333',
    lineHeight: 1.55,
  },
  detailsToggle: {
    marginTop: '14px',
    padding: '6px 12px',
    background: 'transparent',
    border: '1px solid #ccc',
    borderRadius: '5px',
    color: '#444',
    cursor: 'pointer',
    fontSize: '0.82rem',
    fontFamily: 'inherit',
  },
  detailsBody: {
    marginTop: '14px',
    paddingTop: '12px',
    borderTop: '1px dashed #e0e0e0',
  },
  bucketHeading: {
    fontSize: '0.76rem',
    fontWeight: 700,
    letterSpacing: '0.6px',
    textTransform: 'uppercase',
    color: '#666',
    marginTop: '12px',
    marginBottom: '6px',
  },
};

// ── ReleaseCard sub-component ─────────────────────────────────────────────

function ReleaseCard({ release }) {
  const [open, setOpen] = useState(false);
  const bucketKeys = orderedBucketKeys(release.buckets);
  const hasDetail  = bucketKeys.length > 0;

  return (
    <div data-testid="release-card" data-version={release.version} style={styles.releaseCard}>
      <div style={styles.releaseHead}>
        <span data-testid="release-version" style={styles.versionBadge}>
          v{release.version}
        </span>
        <span data-testid="release-date" style={styles.date}>{release.date}</span>
      </div>
      <h3 data-testid="release-title" style={styles.title}>{release.title}</h3>
      {release.summary && release.summary !== release.title && (
        <p style={styles.summary}>{release.summary}</p>
      )}

      {release.highlights && release.highlights.length > 0 && (
        <div style={styles.highlightsBlock}>
          <div style={styles.highlightsLabel}>Highlights</div>
          <ul data-testid="release-highlights" style={styles.bullets}>
            {release.highlights.map((h, idx) => (
              <li key={idx}>{h}</li>
            ))}
          </ul>
        </div>
      )}

      {hasDetail && (
        <>
          <button
            data-testid="release-details-toggle"
            type="button"
            style={styles.detailsToggle}
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
          >
            {open ? 'Hide details' : 'Show details'}
          </button>
          {open && (
            <div data-testid="release-details" style={styles.detailsBody}>
              {bucketKeys.map((key) => (
                <div key={key}>
                  <div style={styles.bucketHeading}>{key}</div>
                  <ul style={styles.bullets}>
                    {release.buckets[key].map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────

export default function ReleaseNotesView({ onBack, releases }) {
  // Default to the bundled releases.json so the view "just works" without
  // additional wiring.  Tests pass an explicit `releases` array.
  const data = Array.isArray(releases) ? releases : bundledReleases;

  return (
    <div data-testid="release-notes-view" style={styles.wrapper}>
      <div style={styles.header}>
        <button
          data-testid="release-notes-back-button"
          style={styles.backButton}
          onClick={onBack}
        >
          ← Back
        </button>
        <span style={styles.headerTitle}>📝 Release Notes</span>
        <span data-testid="release-notes-count" style={{ fontSize: '0.8rem', color: '#aaa' }}>
          {data.length} release{data.length === 1 ? '' : 's'}
        </span>
      </div>

      <div style={styles.body}>
        {data.length === 0 ? (
          <div data-testid="release-notes-empty" style={styles.emptyState}>
            No release notes have been published yet.  Add a section to{' '}
            <code>CHANGELOG.md</code> and re-run <code>npm run build:releases</code>.
          </div>
        ) : (
          data.map((release) => (
            <ReleaseCard key={release.version} release={release} />
          ))
        )}
      </div>
    </div>
  );
}
